import { create } from "zustand";
import {
  ModerationQueueFlag,
  ModerationQueuePage,
  ModerationReviewResource,
  ModerationReviewStatus,
  ModerationFlag,
  ModerationConfigResponse,
  Blocklist,
  Allowlist,
  ModerationResource,
  ModerationAction,
} from "../api/moderation";

interface ModerationState {
  flags: ModerationFlag[];
  configResponse: ModerationConfigResponse | null;
  blocklist: Blocklist | null;
  allowlist: Allowlist | null;
  loadingFlags: boolean;
  loadingConfig: boolean;
  loadingBlocklist: boolean;
  loadingAllowlist: boolean;
  error: string | null;
  loadFlags: (params?: {
    meetingId?: string;
    action?: ModerationAction;
    limit?: number;
    nextToken?: string;
  }) => Promise<void>;
  /** The review queue: flags that need a decision, newest first. */
  reviewFlags: ModerationQueueFlag[];
  loadingReviewFlags: boolean;
  /**
   * Set when the queue has more pages than were fetched.
   *
   * The grid these rows go into filters and pages client-side, so it cannot ask
   * for more — this is surfaced in the UI instead, so a moderator is told the
   * page is partial rather than shown a short list and left to assume there is
   * nothing left to do.
   */
  reviewNextToken?: string;
  /**
   * Errors from the review queue alone.
   *
   * Separate from `error`, which four other loaders write: a failed blocklist
   * read would otherwise surface as a red banner over a perfectly healthy
   * queue, and stay there.
   */
  reviewError: string | null;
  loadReviewFlags: (status?: ModerationReviewStatus) => Promise<void>;
  loadConfig: () => Promise<void>;
  loadBlocklist: () => Promise<void>;
  loadAllowlist: () => Promise<void>;
}

export const useModerationStore = create<ModerationState>((set) => ({
  flags: [],
  reviewFlags: [],
  loadingReviewFlags: false,
  reviewNextToken: undefined,
  reviewError: null,
  configResponse: null,
  blocklist: null,
  allowlist: null,
  loadingFlags: false,
  loadingConfig: false,
  loadingBlocklist: false,
  loadingAllowlist: false,
  error: null,

  loadFlags: async (params?) => {
    set({ loadingFlags: true, error: null });
    try {
      const response = await ModerationResource.getFlags(params);
      set({ flags: response.data, loadingFlags: false });
    } catch (err) {
      const error = err instanceof Error ? err.message : "Failed to load moderation flags";
      set({ error, loadingFlags: false });
    }
  },

  loadReviewFlags: async (status = "PENDING") => {
    set({ loadingReviewFlags: true, reviewError: null });
    try {
      // Asked for by kind, not fetched unfiltered and sieved here. Chat flags
      // share this table and this index, and chat is by far the highest-volume
      // writer — so one unnarrowed page of 100 can be entirely chat, leaving
      // nothing to show while pictures sit unreviewed a page back. The endpoint
      // pages a narrowed read until it fills the limit, which is exactly this
      // case; two requests is the price of the queue being truthful.
      const [pictures, reports]: ModerationQueuePage[] = await Promise.all([
        ModerationReviewResource.getQueue({
          status,
          contentType: "PROFILE_PICTURE",
          limit: 50,
        }),
        ModerationReviewResource.getQueue({
          status,
          contentType: "USER",
          limit: 50,
        }),
      ]);

      const merged = [...pictures.data, ...reports.data].sort((a, b) =>
        b.createdAt.localeCompare(a.createdAt)
      );

      set({
        reviewFlags: merged,
        // Either kind having more says the view is partial.
        reviewNextToken:
          pictures.paging.nextToken ?? reports.paging.nextToken ?? undefined,
        loadingReviewFlags: false,
      });
    } catch (err) {
      const reviewError =
        err instanceof Error ? err.message : "Failed to load the review queue";
      set({ reviewError, loadingReviewFlags: false });
    }
  },

  loadConfig: async () => {
    set({ loadingConfig: true, error: null });
    try {
      const response = await ModerationResource.getConfig();
      set({ configResponse: response, loadingConfig: false });
    } catch (err) {
      const error = err instanceof Error ? err.message : "Failed to load moderation config";
      set({ error, loadingConfig: false });
    }
  },

  loadBlocklist: async () => {
    set({ loadingBlocklist: true, error: null });
    try {
      const response = await ModerationResource.getBlocklist();
      set({ blocklist: response, loadingBlocklist: false });
    } catch (err) {
      const error = err instanceof Error ? err.message : "Failed to load blocklist";
      set({ error, loadingBlocklist: false });
    }
  },

  loadAllowlist: async () => {
    set({ loadingAllowlist: true, error: null });
    try {
      const response = await ModerationResource.getAllowlist();
      set({ allowlist: response, loadingAllowlist: false });
    } catch (err) {
      const error = err instanceof Error ? err.message : "Failed to load allowlist";
      set({ error, loadingAllowlist: false });
    }
  },
}));
